import { getFilePreviewUrl } from './fileToken';
import miscService from '../services/misc.service';
import { DEFAULT_INVOICE_SETTINGS } from '../config/invoiceConfig';

/**
 * generateInvoice.js — Flywell Logistics
 * Professional A4 Tax Invoice / Commercial Invoice
 * Opens in a new window and triggers browser print.
 *
 * @param {Object} order - Full order object from backend
 */
export async function generateInvoice(order) {
  // ── Helpers ──────────────────────────────────────────────────────────
  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const pick = (obj, ...keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return "";
  };

  const line = (label, value) =>
    value
      ? `<div class="info-line"><span class="info-label">${label}</span><span class="info-value">${value}</span></div>`
      : "";

  // Display-only: convert amount to Indian-system words (rupees + paise).
  // Renders the existing grand total only — no calculation logic changed.
  const numberToWordsINR = (num) => {
    const n = Number(num || 0);
    const ones = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
      "Seventeen", "Eighteen", "Nineteen",
    ];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (x) => (x < 20 ? ones[x] : tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : ""));
    const three = (x) => {
      const h = Math.floor(x / 100);
      const r = x % 100;
      return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
    };

    const [rs, ps] = n.toFixed(2).split(".");
    const rupees = parseInt(rs, 10);
    const paise = parseInt(ps, 10);
    if (rupees === 0 && paise === 0) return "Zero Rupees";

    const crore = Math.floor(rupees / 10000000);
    const lakh = Math.floor((rupees % 10000000) / 100000);
    const thousand = Math.floor((rupees % 100000) / 1000);
    const hundred = rupees % 1000;

    const parts = [];
    if (crore) parts.push(two(crore) + " Crore");
    if (lakh) parts.push(two(lakh) + " Lakh");
    if (thousand) parts.push(two(thousand) + " Thousand");
    if (hundred) parts.push(three(hundred));
    let words = parts.join(" ") + " Rupees";
    if (paise > 0) words += " and " + two(paise) + " Paise";
    return words;
  };

  // ── Extract data ─────────────────────────────────────────────────────
  const client = order.client || {};
  const user = order.user || {};
  const pu = order.pickupDetails || {};
  const de = order.deliveryDetails || {};
  const pkg = order.packageDetails || {};
  const pricing = order.pricing || {};
  const payment = order.payment || {};
  const meta = order.metadata || {};

  const isInternational = order.orderType === "international";

  // Find document number by type (searches both client and user docs)
  const docNum = (type) => {
    const search = (docs) => {
      if (!docs || !docs.length) return "";
      const d = docs.find((doc) => {
        const t = (doc.type || "").toUpperCase();
        return t === type.toUpperCase() || (type === "PAN" && t === "PAN_CARD");
      });
      return d?.number || "";
    };
    return search(client?.documents) || search(user.documents);
  };

  const hasClient = !!(client && client._id);

  // Shipper (seller) — prefer client, fallback to user account details
  const shipper = {
    company: pick(client, "companyName") || (hasClient ? pick(client, "name") : "") || pick(user, "name"),
    contact: pick(client, "name") || pick(user, "name"),
    gstin: pick(client, "gstin") || docNum("GST"),
    iec: docNum("IEC"),
    pan: docNum("PAN"),
    address: pick(client, "address") || pick(user, "businessAddress"),
    address2: "",
    city: pick(client, "city"),
    state: pick(client, "state"),
    pincode: pick(client, "pincode"),
    country: pick(client, "country") || "India",
    phone: pick(client, "phone") || pick(user, "phone"),
    email: pick(client, "email") || pick(user, "email"),
  };

  // Pickup address (only if different from shipper)
  const pickupDiff =
    pu.address && pu.address !== shipper.address;
  const pickup = {
    contact: pick(pu, "contactPerson", "name"),
    company: pick(pu, "companyName"),
    address: pick(pu, "address"),
    address2: pick(pu, "addressLine2"),
    city: pick(pu, "city"),
    state: pick(pu, "state"),
    pincode: pick(pu, "pincode"),
    phone: pick(pu, "phone"),
    email: pick(pu, "email"),
  };

  // Consignee
  const consignee = {
    name: pick(de, "name"),
    company: pick(de, "companyName"),
    address: pick(de, "address"),
    address2: pick(de, "addressLine2"),
    city: pick(de, "city"),
    state: pick(de, "state"),
    pincode: pick(de, "pincode"),
    country: pick(de, "country") || "India",
    phone: pick(de, "phone"),
    email: pick(de, "email"),
    vatId: pick(de, "vatId"),
    ioss: pick(de, "iossNumber"),
  };

  // Shipment
  const courier =
    pick(meta, "courierName", "nimbusCourierName") || order.deliveryPartner || "—";
  const awb = order.awb || "—";
  const packageCount = order.products?.length || pkg.shipmentType || 1;

  // Products
  const products =
    order.products && order.products.length > 0
      ? order.products
      : [
          {
            description: pkg.description || "General Merchandise",
            hsnCode: "",
            qty: packageCount,
            unitRate: pricing.totalAmount || 0,
            pieceWt: 0,
          },
        ];

  const productValue = products.reduce(
    (sum, p) => sum + (p.qty || 1) * (p.unitRate || 0),
    0
  );

  // Pricing
  const baseRate = pricing.baseRate || 0;
  const additionalCharges = pricing.additionalCharges || 0;
  const subtotal = baseRate + additionalCharges;
  const taxableAmount = subtotal + productValue;
  const grandTotal = pricing.totalAmount || taxableAmount;

  // Tax — derive from grand total
  let cgst = 0,
    sgst = 0,
    igst = 0;
  if (isInternational) {
    igst = Math.round((grandTotal - grandTotal / 1.18) * 100) / 100;
  } else if (grandTotal > 0) {
    const taxTotal = Math.round((grandTotal - grandTotal / 1.18) * 100) / 100;
    cgst = Math.round((taxTotal / 2) * 100) / 100;
    sgst = taxTotal - cgst;
  }

  const currency = pricing.currency || pkg.currency || "INR";
  const currSymbol = currency === "INR" ? "₹" : currency === "USD" ? "$" : "€";

  // Paid / Balance — display-only, derived from existing payment.status
  const paymentStatus = payment.status || "pending";
  const amountPaid = paymentStatus === "completed" ? grandTotal : 0;
  const balanceAmount = grandTotal - amountPaid;
  const amountInWords = currency === "INR" ? numberToWordsINR(grandTotal) : "";

  // Build signature image URL (client signature takes priority, fallback to user
  // signature). Uses short-lived file tokens so the session JWT never enters URLs.
  const signatureImgUrl = await (async () => {
    // Client signature (from B2B client) — check uploadedAt rather than fileId since fileId is select:false
    if (client?._id && client?.signature?.uploadedAt) {
      return getFilePreviewUrl(`/clients/${client._id}/signature`);
    }
    // User self-signature — check uploadedAt rather than fileId
    if (user?._id && user?.signature?.uploadedAt) {
      return getFilePreviewUrl('/auth/signature');
    }
    return null;
  })();


  // ── Company details (admin-configured Invoice Settings; fall back to defaults) ──
  let fetchedSettings = null;
  try {
    fetchedSettings = await miscService.getInvoiceSettings();
  } catch (_) { /* backend unreachable → defaults */ }

  const company = { ...DEFAULT_INVOICE_SETTINGS };
  if (fetchedSettings && typeof fetchedSettings === 'object') {
    for (const key of Object.keys(company)) {
      const v = fetchedSettings[key];
      if (typeof v === 'string' && v.trim() !== '') company[key] = v.trim();
    }
  }

  // Conditional fragments — rendered only when the value is non-empty
  const companyMetaParts = [
    company.website ? `Web: ${company.website}` : '',
    company.email ? `Email: ${company.email}` : '',
    company.gstin ? `GSTIN: <span class="id">${company.gstin}</span>` : '',
    company.pan ? `PAN: <span class="id">${company.pan}</span>` : '',
    company.cin ? `CIN: <span class="id">${company.cin}</span>` : ''
  ].filter(Boolean);
  const companyMetaHtml = companyMetaParts.length > 2
    ? companyMetaParts.slice(0, 2).join(' &nbsp;·&nbsp; ') + '<br/>' + companyMetaParts.slice(2).join(' &nbsp;·&nbsp; ')
    : companyMetaParts.join(' &nbsp;·&nbsp; ');
  const officeAddressRow = company.address ? `<span class="row"><b>${company.address}</b></span>` : '';
  const officeStateRow = (company.state || company.stateCode)
    ? `<span class="row">State: ${company.state}${company.state && company.stateCode ? ' &nbsp;·&nbsp; ' : ''}${company.stateCode ? `State Code: ${company.stateCode}` : ''}</span>`
    : '';
  const officeContactRow = (company.phone || company.email)
    ? `<span class="row">${[company.phone, company.email].filter(Boolean).join(' &nbsp;·&nbsp; ')}</span>`
    : '';
  const footerContact = [company.website, company.email, company.phone].filter(Boolean).join(' &nbsp;|&nbsp; ');
  const footerIds = [
    company.gstin ? `GSTIN: ${company.gstin}` : '',
    company.pan ? `PAN: ${company.pan}` : '',
    company.cin ? `CIN: ${company.cin}` : '',
    company.stateCode ? `State Code: ${company.stateCode}` : ''
  ].filter(Boolean).join(' &nbsp;|&nbsp; ');
  const bankBox = company.accountNumber ? `
  <div class="bank-box">
    <div class="bank-title">Bank Details</div>
    <div class="bank-grid">
      ${company.bankName ? `<span><b>Bank:</b> ${company.bankName}</span>` : ''}
      ${company.accountHolder ? `<span><b>A/C Holder:</b> ${company.accountHolder}</span>` : ''}
      <span><b>A/C No:</b> ${company.accountNumber}</span>
      ${company.ifsc ? `<span><b>IFSC:</b> ${company.ifsc}</span>` : ''}
      ${company.branch ? `<span><b>Branch:</b> ${company.branch}</span>` : ''}
      ${company.upi ? `<span><b>UPI:</b> ${company.upi}</span>` : ''}
    </div>
  </div>` : '';

  // ── HTML ─────────────────────────────────────────────────────────────
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>Tax Invoice — ${order.orderNumber || "—"}</title>
<style>
  /* ── Reset & Base ─────────────────────────────────────────────── */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: "Inter", "Segoe UI", system-ui, -apple-system, sans-serif;
    font-size: 12px;
    color: #1e293b;
    background: #fff;
  }
  /* Fixed A4 sheet — 210mm x 297mm portrait. Inner padding = paper margins,
     so the sheet prints edge-to-edge and looks like a normal white A4 paper. */
  .page {
    width: 210mm;
    min-height: 296mm;
    margin: 0 auto;
    padding: 12mm 10mm 8mm;
    background: #fff;
    display: flex;
    flex-direction: column;
  }

  /* ── Header ──────────────────────────────────────────────────── */
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding: 24px 28px 20px;
    background: #fff;
    border-bottom: 3px solid #2563eb;
  }
  .header-left .logo { height: 44px; margin-bottom: 10px; }
  .company-name {
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: .2px;
  }
  .company-meta {
    margin-top: 4px;
    font-size: 10.5px;
    color: #475569;
    line-height: 1.7;
  }
  .company-meta .id { font-weight: 600; color: #1e293b; }
  .header-right { text-align: right; }
  .doc-type {
    font-size: 24px;
    font-weight: 800;
    color: #2563eb;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    line-height: 1.15;
  }
  .doc-sub {
    display: inline-block;
    margin-top: 6px;
    padding: 2px 12px;
    background: #0d9488;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    border-radius: 100px;
  }
  .office {
    margin-top: 8px;
    font-size: 10px;
    color: #475569;
    line-height: 1.7;
  }
  .office .row { display: block; }
  .office b { color: #1e293b; }

  /* ── Bill To / Invoice meta ──────────────────────────────────── */
  .info-grid { display: flex; border-bottom: 1px solid #e2e8f0; }
  .info-col { padding: 18px 28px; }
  .info-col.left { flex: 1.3; border-right: 1px solid #e2e8f0; }
  .info-col.right { flex: 1; }
  .col-title {
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: #2563eb;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 2px solid #e2e8f0;
  }
  .info-line { display: flex; margin-bottom: 4px; font-size: 11.5px; line-height: 1.55; }
  .info-label { color: #64748b; min-width: 92px; flex-shrink: 0; }
  .info-value { font-weight: 600; color: #1e293b; word-break: break-word; }
  .meta-table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
  .meta-table td { padding: 3.5px 0; vertical-align: top; }
  .meta-table td.l { color: #64748b; width: 42%; }
  .meta-table td.v { font-weight: 600; color: #1e293b; text-align: right; word-break: break-word; }

  /* ── Items table ─────────────────────────────────────────────── */
  .table-wrap { padding: 18px 28px 4px; }
  .section-title {
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: #1e293b;
    margin: 16px 28px 10px;
  }
  .items-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  .items-table th {
    background: #eef2f7;
    color: #334155;
    font-weight: 700;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: .5px;
    padding: 9px 10px;
    border-top: 1px solid #dbe3ee;
    border-bottom: 1px solid #dbe3ee;
    text-align: left;
  }
  .items-table th.right, .items-table td.right { text-align: right; }
  .items-table th.center, .items-table td.center { text-align: center; }
  .items-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #eef2f4;
    vertical-align: top;
    word-break: break-word;
  }
  .items-table tbody tr:nth-child(even) td { background: #fafbfd; }
  .items-table tr { page-break-inside: avoid; }
  thead { display: table-header-group; }

  /* ── Totals ──────────────────────────────────────────────────── */
  .totals-area { padding: 12px 28px 4px; }
  .totals-box {
    width: 46%;
    margin-left: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    page-break-inside: avoid;
  }
  .totals-row {
    display: flex;
    justify-content: space-between;
    padding: 6px 14px;
    font-size: 11.5px;
    border-bottom: 1px solid #f1f5f9;
  }
  .totals-row span:first-child { color: #475569; }
  .totals-row span:last-child { font-weight: 600; color: #1e293b; }
  .totals-row.grand {
    background: #eef5ff;
    border-bottom: none;
    padding: 9px 14px;
  }
  .totals-row.grand span:first-child {
    font-weight: 800;
    color: #1e3a8a;
    text-transform: uppercase;
    font-size: 10.5px;
    letter-spacing: .6px;
  }
  .totals-row.grand span:last-child { font-weight: 800; color: #2563eb; font-size: 14px; }
  .totals-row.paid { background: #f8fafc; }
  .totals-row.paid span:last-child { color: #047857; }
  .totals-row.due span:last-child { color: #b91c1c; }
  .words {
    margin: 10px 28px 0;
    padding: 10px 14px;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    font-size: 10.5px;
    color: #475569;
    line-height: 1.7;
  }
  .words b { color: #0f172a; }

  /* ── Signature Block ─────────────────────────────────────────── */
  .sig-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 12px 28px 26px;
    margin-top: auto;
  }
  .sig-block { text-align: center; }
  .sig-line {
    width: 170px;
    border-bottom: 1px solid #94a3b8;
    margin: 0 auto 6px;
    padding-top: 30px;
  }
  .sig-label { font-size: 10px; color: #475569; font-weight: 700; }
  .seal-box {
    width: 84px;
    height: 84px;
    border: 2px dashed #cbd5e1;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: .5px;
  }

  /* ── Footer ──────────────────────────────────────────────────── */
  .footer {
    background: #0f2b47;
    color: #cbd5e1;
    padding: 14px 28px;
    text-align: center;
    font-size: 10px;
    line-height: 1.8;
  }
  .footer strong { color: #fff; }
  .footer .disclaimer {
    margin-top: 6px;
    font-size: 9px;
    color: #94a3b8;
    font-style: italic;
  }

  /* ── Status Badge ────────────────────────────────────────────── */
  .badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 100px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .4px;
  }
  .badge-paid { background: #dcfce7; color: #166534; }
  .badge-pending { background: #fef3c7; color: #92400e; }
  .badge-failed { background: #fecaca; color: #991b1b; }

  /* ── Bank Details Box ─────────────────────────────────────────── */
  .bank-box {
    margin: 14px 28px 0;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }
  .bank-title {
    background: #f8fafc;
    padding: 6px 12px;
    font-size: 10px;
    font-weight: 700;
    color: #334155;
    border-bottom: 1px solid #e2e8f0;
  }
  .bank-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 16px;
    padding: 8px 12px;
    font-size: 10px;
    color: #475569;
  }
  .terms {
    margin: 8px 28px 0;
    font-size: 9px;
    color: #94a3b8;
    text-align: justify;
    white-space: pre-line;
  }

  /* ── Screen preview (popup before printing) ─────────────────── */
  @media screen {
    body { background: #f1f5f9; padding: 24px; }
    .page { box-shadow: 0 4px 24px rgba(0,0,0,.08); }
  }

  /* ── Print — exact A4 sheet ─────────────────────────────────── */
  @page { size: A4 portrait; margin: 0; }
  @media print {
    html, body { width: 210mm; margin: 0; padding: 0; background: #fff; }
    .page { box-shadow: none; margin: 0; }
    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .header, .footer, .info-grid, .table-wrap, .totals-box,
    .words, .sig-row, .bank-box, .terms {
      page-break-inside: avoid;
    }
  }
</style>
</head>
<body>
<div class="page">

  <!-- ═══════════════ HEADER ═══════════════ -->
  <div class="header">
    <div class="header-left">
      <img class="logo" src="/images/logoAuth.png" alt="Flywell Logistics" />
      <div class="company-name">${company.companyName}</div>
      <div class="company-meta">${companyMetaHtml}</div>
    </div>
    <div class="header-right">
      <div class="doc-type">${isInternational ? "Commercial Invoice" : "Tax Invoice"}</div>
      <div class="doc-sub">Original for recipient</div>
      <div class="office">
        ${officeAddressRow}
        ${officeStateRow}
        ${officeContactRow}
      </div>
    </div>
  </div>

  <!-- ═══════════════ CUSTOMER + INVOICE META ═══════════════ -->
  <div class="info-grid">
    <div class="info-col left">
      <div class="col-title">Customer Details</div>
      ${line("Name", shipper.company || shipper.contact)}
      ${line("Contact", shipper.contact)}
      <div class="info-line">
        <span class="info-label">Address</span>
        <span class="info-value">${[
          shipper.address,
          shipper.city,
          shipper.state,
          shipper.pincode,
          shipper.country,
        ].filter(Boolean).join(", ")}</span>
      </div>
      ${line("GSTIN", shipper.gstin)}
      ${line("Place of Supply", consignee.state || consignee.city)}
    </div>
    <div class="info-col right">
      <div class="col-title">Invoice Details</div>
      <table class="meta-table">
        <tr>
          <td class="l">Invoice No.</td>
          <td class="v" style="color:#2563eb;">${order.orderNumber || "—"}</td>
        </tr>
        <tr>
          <td class="l">Invoice Date</td>
          <td class="v">${fmtDate(order.createdAt)}</td>
        </tr>
        <tr>
          <td class="l">Invoice Type</td>
          <td class="v">${isInternational ? "Commercial Invoice" : "Tax Invoice"}</td>
        </tr>
        <tr>
          <td class="l">Payment Status</td>
          <td class="v">
            <span class="badge ${payment.status === "completed" ? "badge-paid" : payment.status === "failed" ? "badge-failed" : "badge-pending"}">${payment.status || "—"}</span>
          </td>
        </tr>
        <tr>
          <td class="l">Payment Method</td>
          <td class="v" style="text-transform:capitalize;">${payment.method || "—"}</td>
        </tr>
        <tr>
          <td class="l">AWB / Tracking No.</td>
          <td class="v">${awb}</td>
        </tr>
        <tr>
          <td class="l">Courier Partner</td>
          <td class="v">${courier}</td>
        </tr>
      </table>
    </div>
  </div>

  <!-- ═══════════════ PICKUP (if different) ═══════════════ -->
  ${pickupDiff ? `
  <div class="info-grid" style="border-bottom:none;">
    <div class="info-col left">
      <div class="col-title">Pickup Details</div>
      ${line("Contact", pickup.contact)}
      ${line("Company", pickup.company)}
      ${line("Phone", pickup.phone)}
      ${line("Email", pickup.email)}
    </div>
    <div class="info-col right">
      <div class="col-title">Pickup Address</div>
      <div class="info-line">
        <span class="info-label">Address</span>
        <span class="info-value">${[
          pickup.address,
          pickup.address2,
          pickup.city,
          pickup.state,
          pickup.pincode,
        ].filter(Boolean).join(", ")}</span>
      </div>
    </div>
  </div>
  ` : ""}

  <!-- ═══════════════ ITEM DETAILS ═══════════════ -->
  <div class="section-title">Item Details</div>
  <div class="table-wrap">
    <table class="items-table">
      <thead>
        <tr>
          <th class="center" style="width:4%;">#</th>
          <th style="width:30%;">Description</th>
          <th class="center" style="width:11%;">HSN / SAC</th>
          <th class="center" style="width:8%;">Qty</th>
          <th class="right" style="width:13%;">Unit Cost</th>
          <th class="right" style="width:16%;">Taxable Value</th>
          <th class="right" style="width:18%;">Line Total</th>
        </tr>
      </thead>
      <tbody>
        ${products
          .map(
            (p, i) => `
          <tr>
            <td class="center">${i + 1}</td>
            <td>${p.description || "—"}</td>
            <td class="center">${p.hsnCode || "—"}</td>
            <td class="center">${p.qty || 1}</td>
            <td class="right">${currSymbol} ${fmt(p.unitRate)}</td>
            <td class="right">${currSymbol} ${fmt((p.qty || 1) * (p.unitRate || 0))}</td>
            <td class="right" style="font-weight:600;">${currSymbol} ${fmt((p.qty || 1) * (p.unitRate || 0))}</td>
          </tr>`
          )
          .join("")}
      </tbody>
    </table>
  </div>

  <!-- ═══════════════ TOTALS ═══════════════ -->
  <div class="totals-area">
    <div class="totals-box">
      <div class="totals-row">
        <span>Product Value</span>
        <span>${currSymbol} ${fmt(productValue)}</span>
      </div>
      <div class="totals-row">
        <span>Shipping Charges</span>
        <span>${currSymbol} ${fmt(baseRate)}</span>
      </div>
      ${additionalCharges > 0 ? `
      <div class="totals-row">
        <span>Other Charges</span>
        <span>${currSymbol} ${fmt(additionalCharges)}</span>
      </div>
      ` : ""}
      <div class="totals-row">
        <span>Taxable Amount</span>
        <span>${currSymbol} ${fmt(taxableAmount)}</span>
      </div>
      ${isInternational
        ? `
      <div class="totals-row">
        <span>IGST (18%)</span>
        <span>${currSymbol} ${fmt(igst)}</span>
      </div>`
        : `
      <div class="totals-row">
        <span>CGST (9%)</span>
        <span>${currSymbol} ${fmt(cgst)}</span>
      </div>
      <div class="totals-row">
        <span>SGST (9%)</span>
        <span>${currSymbol} ${fmt(sgst)}</span>
      </div>`}
      <div class="totals-row grand">
        <span>Total</span>
        <span>${currSymbol} ${fmt(grandTotal)}</span>
      </div>
      <div class="totals-row paid">
        <span>Paid Amount</span>
        <span>${currSymbol} ${fmt(amountPaid)}</span>
      </div>
      <div class="totals-row ${balanceAmount > 0 ? "due" : "paid"}">
        <span>Balance</span>
        <span>${currSymbol} ${fmt(balanceAmount)}</span>
      </div>
    </div>
  </div>

  <!-- ═══════════════ AMOUNT IN WORDS ═══════════════ -->
  ${amountInWords ? `
  <div class="words">
    <b>Amount (In Words):</b> ${amountInWords} Only
  </div>
  ` : ""}

  ${bankBox}
  ${company.termsNote ? `<div class="terms">${company.termsNote}</div>` : ""}

  <!-- ═══════════════ SIGNATURE ═══════════════ -->
  <div class="sig-row">
    <div class="sig-block">
      ${signatureImgUrl
        ? `<img src="${signatureImgUrl}" alt="Authorized Signature" style="max-width:180px;max-height:70px;object-fit:contain;margin-bottom:4px;" onerror="this.style.display='none';this.nextElementSibling.style.display='block';" />
           <div class="sig-line" style="display:none;"></div>`
        : `<div class="sig-line"></div>`
      }
      <div class="sig-label">Authorized Signatory</div>
      <div style="font-size:10px;color:#94a3b8;margin-top:2px;">${shipper.company || shipper.contact || company.companyName}</div>
    </div>
    <div class="sig-block">
      ${signatureImgUrl
        ? `<img src="${signatureImgUrl}" alt="Company Stamp" style="max-width:84px;max-height:84px;object-fit:contain;border-radius:50%;margin-bottom:4px;" onerror="this.style.display='none';" />`
        : `<div class="seal-box">Company<br/>Seal</div>`
      }
      <div class="sig-label" style="margin-top:6px;">Date: ${fmtDate(new Date())}</div>
    </div>
  </div>

  <!-- ═══════════════ FOOTER ═══════════════ -->
  <div class="footer">
    <strong>${company.companyName}</strong><br/>
    ${footerContact ? `<span>${footerContact}</span><br/>` : ""}
    <span>${footerIds}${footerIds ? " &nbsp;|&nbsp; " : ""}AWB / LRN: ${awb}</span>
    <div class="disclaimer">
      This is a computer-generated invoice and does not require a physical signature.
    </div>
  </div>

</div><!-- /page -->

<script>
  (function () {
    var imgs = Array.prototype.slice.call(document.images);
    Promise.all(imgs.map(function (i) {
      if (i.complete) return Promise.resolve();
      return new Promise(function (res) { i.onload = res; i.onerror = res; });
    })).then(function () { window.print(); });
  })();
</script>
</body>
</html>`;

  // ── Open & Print ─────────────────────────────────────────────────────
  const win = window.open("", "_blank", "width=1000,height=800");
  if (!win) {
    alert("Pop-up blocked! Please allow pop-ups for this site to print the invoice.");
    return;
  }
  win.document.write(html);
  win.document.close();
}
